import { useState, useEffect } from 'react';
import { Check, Sparkles, FileCode, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useExtractionTemplates,
  useExtractionTemplate,
  useApplyTemplate,
} from '@/hooks/use-extraction-templates';
import { toast } from 'sonner';

export interface TemplateSelectorProps {
  onTemplateApplied: (query: any) => void;
  currentQuery?: any;
}

export function TemplateSelector({ onTemplateApplied, currentQuery }: TemplateSelectorProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [variables, setVariables] = useState<Record<string, any>>({});

  const { data: templatesData, isLoading } = useExtractionTemplates();
  const { data: selectedTemplate } = useExtractionTemplate(selectedTemplateId);
  const applyTemplate = useApplyTemplate(selectedTemplateId);

  const templates = templatesData?.templates || [];

  // Initialize variables with defaults when template changes
  useEffect(() => {
    if (selectedTemplate?.variables) {
      const defaultVars: Record<string, any> = {};
      selectedTemplate.variables.forEach((v) => {
        if (v.default !== undefined) {
          defaultVars[v.name] = v.default;
        }
      });
      setVariables(defaultVars);
    }
  }, [selectedTemplate]);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setIsPreviewOpen(true);
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      // If template has variables, apply with variables
      if (selectedTemplate.variables && selectedTemplate.variables.length > 0) {
        const result = await applyTemplate.mutateAsync({ variables });
        onTemplateApplied(result.query);
        toast.success(`Template "${selectedTemplate.name}" applied successfully`);
      } else {
        // No variables, use template directly
        onTemplateApplied(selectedTemplate.template);
        toast.success(`Template "${selectedTemplate.name}" applied successfully`);
      }

      setIsPreviewOpen(false);
      setSelectedTemplateId('');
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'government':
        return 'bg-blue-100 text-blue-800';
      case 'ecommerce':
        return 'bg-green-100 text-green-800';
      case 'social':
        return 'bg-purple-100 text-purple-800';
      case 'custom':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Quick Start Templates</Label>
          <Badge variant="outline" className="text-xs">
            <Sparkles className="h-3 w-3 mr-1" />
            {templates.length} templates
          </Badge>
        </div>

        <Select value={selectedTemplateId} onValueChange={handleTemplateSelect}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Choose a template to get started..." />
          </SelectTrigger>
          <SelectContent>
            {isLoading ? (
              <SelectItem value="loading" disabled>
                Loading templates...
              </SelectItem>
            ) : templates.length === 0 ? (
              <SelectItem value="empty" disabled>
                No templates available
              </SelectItem>
            ) : (
              templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`text-xs ${getCategoryColor(template.category)}`}
                    >
                      {template.category}
                    </Badge>
                    <span>{template.name}</span>
                  </div>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        {currentQuery && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Applying a template will replace your current query configuration.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Template Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCode className="h-5 w-5" />
              {selectedTemplate?.name}
            </DialogTitle>
            <DialogDescription>{selectedTemplate?.description}</DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-4">
              {/* Category & API Type */}
              <div className="flex gap-2">
                <Badge className={getCategoryColor(selectedTemplate.category)}>
                  {selectedTemplate.category}
                </Badge>
                <Badge variant="outline">{selectedTemplate.apiType}</Badge>
              </div>

              {/* Variables Input */}
              {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium">Template Variables</h4>
                  {selectedTemplate.variables.map((variable) => (
                    <div key={variable.name} className="space-y-1">
                      <Label className="text-xs font-medium">
                        <span className="font-mono text-xs text-muted-foreground">
                          {variable.name}
                        </span>
                        {variable.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <p className="text-xs text-muted-foreground">{variable.description}</p>
                      <Input
                        type={variable.type === 'number' ? 'number' : 'text'}
                        value={variables[variable.name] ?? ''}
                        onChange={(e) =>
                          setVariables({
                            ...variables,
                            [variable.name]:
                              variable.type === 'number'
                                ? e.target.value === ''
                                  ? undefined
                                  : parseFloat(e.target.value)
                                : e.target.value,
                          })
                        }
                        placeholder={
                          variable.default !== undefined
                            ? `default: ${variable.default}`
                            : `Enter ${variable.name}`
                        }
                        className="text-sm font-mono"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Template Preview */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Template Configuration</h4>
                <Textarea
                  value={JSON.stringify(selectedTemplate.template, null, 2)}
                  readOnly
                  className="font-mono text-xs h-48"
                />
              </div>

              {/* Example (if available) */}
              {selectedTemplate.example && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Example Output</h4>
                  <div className="bg-gray-50 p-3 rounded text-xs font-mono overflow-x-auto">
                    <pre>{JSON.stringify(selectedTemplate.example.output, null, 2)}</pre>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleApplyTemplate} disabled={applyTemplate.isPending}>
                  <Check className="h-4 w-4 mr-2" />
                  {applyTemplate.isPending ? 'Applying...' : 'Apply Template'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
